'use client'

import { getCookieConsentTranslationsConfig } from '@/actions/getCookieConsentTranslations'
import { useConsent } from '@/contexts/ConsentContext'
import { mixpanelClient } from '@/lib/analytics/mixpanelClient'
import {
  CONSENT_CATEGORIES,
  CONSENT_SERVICES,
} from '@/lib/consent/consentConstants'
import { useLocale } from 'next-intl'
import { useEffect } from 'react'
import * as CookieConsent from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'

const { CAT_ANALYTICS, CAT_FUNCTIONALITY, CAT_NECESSARY } = CONSENT_CATEGORIES
const { SERVICE_FUNCTIONALITY_STORAGE, SERVICE_ANALYTICS_STORAGE } =
  CONSENT_SERVICES

export default function CookieConsentAndTracking() {
  const locale = useLocale()
  const { updateConsent } = useConsent()

  useEffect(() => {
    mixpanelClient.init()
  }, [])

  useEffect(() => {
    const handleConsentChange = () => {
      updateConsent()
      const analyticsAccepted = CookieConsent.acceptedCategory('analytics')
      if (analyticsAccepted) {
        mixpanelClient.optInTracking()
      } else {
        mixpanelClient.optOutTracking()
      }
    }

    const initCookieConsent = async () => {
      const translationsConfig = await getCookieConsentTranslationsConfig()

      const config: CookieConsent.CookieConsentConfig = {
        // NOTE: Increment `revision` number to trigger the consent modal again when we have made changes to our cookie policy
        revision: 1,
        onFirstConsent: handleConsentChange,
        onConsent: handleConsentChange,
        onChange: handleConsentChange,
        guiOptions: {
          consentModal: {
            layout: 'box wide',
            position: 'bottom right',
          },
        },
        categories: {
          [CAT_NECESSARY]: {
            enabled: true, // this category is enabled by default
            readOnly: true, // this category cannot be disabled
          },
          [CAT_ANALYTICS]: {
            autoClear: {
              cookies: [
                {
                  name: /^_ga/, // regex: match all cookies starting with '_ga'
                },
                {
                  name: '_gid', // string: exact cookie name
                },
              ],
            },
            services: {
              [SERVICE_ANALYTICS_STORAGE]: {
                label:
                  'Enables storage (such as cookies) related to analytics e.g. visit duration.',
              },
            },
          },
          [CAT_FUNCTIONALITY]: {
            services: {
              [SERVICE_FUNCTIONALITY_STORAGE]: {
                label:
                  'Enables storage that supports the functionality of the website e.g. language settings.',
              },
            },
          },
        },
        language: translationsConfig,
      }

      CookieConsent.run(config)
    }

    initCookieConsent()
  }, [updateConsent, locale])

  return null
}
